/**
 * PC端桥接模块（简化版）
 * 提供与移动端 ~bridge@1.2 一致的基础API接口
 * @module bridge/1.0
 * @author PLC
 * @version 1.0
 */

import M_ENV from '~env@1.0'

/**
 * 判断是否为iOS V2调用方式
 */
function isIosV2Invok() {
    const ua = navigator.userAgent
    return /wv_i_v2/i.test(ua)
}

/**
 * 检测是否在App中
 */
function checkInApp() {
    return M_ENV.isNativeInApp || M_ENV.isWebInApp
}

/**
 * 调用App接口
 * @param {String} method - 调用的接口名
 * @param {Object} options - 调用接口传入的参数
 */
function invoke(method, options = {}) {
    if (!checkInApp()) {
        console.log(`[Bridge] Not in App, skip invoke: ${method}`)
        return
    }

    try {
        // 触发页面跳转事件
        const event = new Event('locationHrefChange')
        document.dispatchEvent(event)

        // iOS调用方式
        if (isIosV2Invok() || M_ENV.isNativeInIOS) {
            if (window.webkit?.messageHandlers?.FQL_JSBridge) {
                const params = {
                    funcName: method,
                    parameter: JSON.stringify(options)
                }
                window.webkit.messageHandlers.FQL_JSBridge.postMessage(params)
            } else {
                console.warn('[Bridge] iOS bridge not available')
            }
        }
        // Android调用方式
        else if (M_ENV.isNativeInAndroid) {
            if (window.FQL_JSBridge && window.FQL_JSBridge[method]) {
                const params = typeof options === 'object' ? JSON.stringify(options) : options
                window.FQL_JSBridge[method](params)
            } else {
                console.warn('[Bridge] Android bridge not available')
            }
        }
        // WebInApp - 尝试调用WebView的JSBridge
        else if (M_ENV.isWebInApp) {
            const topWindow = window.top || window
            if (topWindow.webkit?.messageHandlers?.FQL_JSBridge) {
                // iOS WebView
                topWindow.webkit.messageHandlers.FQL_JSBridge.postMessage({
                    funcName: method,
                    parameter: JSON.stringify(options)
                })
            } else if (topWindow.FQL_JSBridge && topWindow.FQL_JSBridge[method]) {
                // Android WebView
                const params = typeof options === 'object' ? JSON.stringify(options) : options
                topWindow.FQL_JSBridge[method](params)
            } else {
                console.warn('[Bridge] WebView bridge not available')
            }
        }
    } catch (e) {
        console.error('[Bridge] Invoke error:', e)
    }
}

/**
 * Lotus模块
 * 提供navigator.back()等方法
 */
const Lotus = {
    /**
     * 返回上一页
     * @param {Object} options - 选项（可选）
     */
    navigator: {
        back(options = {}) {
            // 统一使用bridge的goBack方法
            invoke('goBack', options)
        },

        /**
         * 关闭当前页面
         * @param {Object} options - 选项（可选）
         */
        close(options = {}) {
            // 使用goBack并关闭webview
            invoke('goBack', { isCloseWebview: true, ...options })
        }
    },

    /**
     * 分享功能
     */
    share: {
        shareDetails(options, callback) {
            invoke('shareDetails', options)
            if (callback) {
                // 分享回调
                console.log('[Bridge] Share callback not implemented in web')
            }
        }
    }
}

/**
 * 桥接对象
 */
const M_BRIDGE = {
    /**
     * 调用JSBridge接口
     * @param {String} method - 方法名
     * @param {Object} options - 参数
     * @example
     * M_BRIDGE.invokeJsBridge('hideTitle')
     * M_BRIDGE.invokeJsBridge('adjustiPhoneXBottomColor', {hide_bottom: 1})
     */
    invokeJsBridge(method = '', options = {}) {
        invoke(method, options)
    },

    /**
     * 返回上一页/关闭webview
     * @param {Object} options - 选项
     * @param {Boolean} options.isCloseWebview - 是否关闭当前webview
     * @param {Boolean} options.isPreventH5Default - 是否阻止h5默认行为
     * @example
     * M_BRIDGE.goBack()  // 返回上一页
     * M_BRIDGE.goBack({ isCloseWebview: true })  // 关闭webview
     */
    goBack(options = {}) {
        // WebInApp环境：调用goBack
        if (M_ENV.isWebInApp) {
            if (options.isCloseWebview) {
                invoke('onCloseClicked')
            } else {
                invoke('goBack')
            }
        }
        // Native环境：调用onCloseClicked
        else if (M_ENV.isNativeInApp) {
            invoke('onCloseClicked')
        }
        // 纯Web环境：返回上一页
        else {
            window.history.back()
        }
    },

    /**
     * 隐藏原生标题栏
     */
    hideTitle() {
        this.invokeJsBridge('hideTitle')
    },

    /**
     * 调整iPhoneX底部颜色
     * @param {Object} options - 选项
     * @param {Number} options.hide_bottom - 是否隐藏底部 1=隐藏
     */
    adjustiPhoneXBottomColor(options = {}) {
        this.invokeJsBridge('adjustiPhoneXBottomColor', options)
    },

    /**
     * 关闭当前页面
     */
    closePage() {
        this.goBack({ isCloseWebview: true })
    },

    /**
     * 设置标题
     * @param {String} title - 标题文字
     */
    setTitle(title) {
        this.invokeJsBridge('setTitle', { title })
    },

    /**
     * 打开URL
     * @param {String} url - URL地址
     */
    openUrl(url) {
        this.invokeJsBridge('openUrl', { url })
    },

    /**
     * Lotus模块
     * 提供与移动端一致的lotus接口
     */
    lotus: Lotus
}

export default M_BRIDGE